// @flow
import { toastr } from 'react-redux-toastr';
import { makeApiCall } from '../../../../services/apiService/apiService';
import {
  downloadArrayBufferAsXLS,
  getColumnSum
} from '../../../../services/helpers';
import moment from 'moment';

/**
 * rpCollectionsActions - Redux action types
 * @type {Object}
 * @property {string} SET_LOADER - Set loader
 * @property {string} SET_RTL_COLLECTIONS_DATE_FILTER - Set RTL collections date filter
 * @property {string} GET_AMOUNT_BALANCE - Get amount balance
 * @property {string} CONFIRM_FNF_AMOUNT - Confirm fnf amount
 * @property {string} APPROVE_FNF_AMOUNT - Approve fnf amount
 * @property {string} GET_HANDOVER_PENDING_DATA - Get handover pending data
 * @property {string} GET_HANDOVER_COMPLETED_DATA - Get handover completed data
 * @property {string} SET_FILTER_DATES - Set filter dates
 */

export const rpCollectionsActions = {
  SET_LOADER: 'SET_LOADER',
  SET_RTL_COLLECTIONS_DATE_FILTER: 'SET_RTL_COLLECTIONS_DATE_FILTER',
  GET_AMOUNT_BALANCE: 'GET_AMOUNT_BALANCE',
  CONFIRM_FNF_AMOUNT: 'CONFIRM_FNF_AMOUNT',
  APPROVE_FNF_AMOUNT: 'APPROVE_FNF_AMOUNT',
  GET_HANDOVER_PENDING_DATA: 'GET_HANDOVER_PENDING_DATA',
  GET_HANDOVER_COMPLETED_DATA: 'GET_HANDOVER_COMPLETED_DATA',
  SET_FILTER_DATES: 'SET_FILTER_DATES',
  SET_KNOCK_OFF_LOADER: 'SET_KNOCK_OFF_LOADER',
  SET_RP_LIST: 'SET_RP_LIST'
};

export const setFilterDates = (startDate, endDate) => ({
  type: rpCollectionsActions.SET_FILTER_DATES,
  payload: { startDate, endDate }
});

/**
 * @function getAmountBalance - Get amount balance of the user
 * @functionality - Set amountBalance variable in the rpCollectionsReducer to the amount balance of the user
 * @param none
 * @returns void
 */

export const getAmountBalance = (orgId, expenseType) => dispatch => {
  dispatch({
    type: rpCollectionsActions.GET_AMOUNT_BALANCE,
    payload: { isLoadingBalance: true, amountBalance: null }
  });

  let params = [{ key: '{expenseType}', val: expenseType }];
  let apiEndPoint = 'getRPCollectionAmountBalance';

  if (orgId) {
    params.push({ key: '{orgId}', val: orgId });
    apiEndPoint = 'getRPCollectionAmountBalanceAdmin';
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    successCb: res => {
      console.log('getAmountBalance', res);
      if (
        res.status === 'SUCCESS' &&
        res.payload &&
        res.payload.balance !== null
      ) {
        dispatch({
          type: rpCollectionsActions.GET_AMOUNT_BALANCE,
          payload: {
            isLoadingBalance: false,
            amountBalance: res.payload.balance,
            vanNumber: res.payload.orgCode
              ? 'MLLESP' +
                res.payload.expenseType +
                res.payload.orgCode.replace('-', '')
              : null,
            fnfSettlementDto: res.payload.fnfSettlementResponseDto,
            orgCode: res.payload.orgCode
          }
        });
      } else {
        dispatch({
          type: rpCollectionsActions.GET_AMOUNT_BALANCE,
          payload: {
            isLoadingBalance: false,
            isLoaded: true,
            amountBalance: null,
            fnfSettlementDto: null
          }
        });
        toastr.error('Error', 'Failed to get amount balance');
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.GET_AMOUNT_BALANCE,
        payload: {
          isLoadingBalance: false,
          isLoaded: true,
          amountBalance: null,
          fnfSettlementDto: null
        }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.GET_AMOUNT_BALANCE,
        payload: {
          isLoadingBalance: false,
          isLoaded: true,
          amountBalance: null,
          fnfSettlementDto: null
        }
      });
    }
  };

  return makeApiCall(requestObj);
};

export const confirmFnfAmount = (orgId, expenseType, amount) => dispatch => {
  dispatch({
    type: rpCollectionsActions.CONFIRM_FNF_AMOUNT,
    payload: { isLoadingBalance: true }
  });

  let requestMethod = 'POST';
  let params = [{ key: '{expenseType}', val: expenseType }];
  let apiEndPoint = 'confirmRPCollectionFnf';
  if (orgId) {
    params.push({ key: '{orgCode}', val: orgId });
    params.push({ key: '{amount}', val: amount });
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    method: requestMethod,
    successCb: res => {
      if (
        res.status === 'SUCCESS' &&
        res.payload &&
        res.payload.balance !== null
      ) {
        dispatch({
          type: rpCollectionsActions.CONFIRM_FNF_AMOUNT,
          payload: {
            isLoadingBalance: false,
            amountBalance: 0,
            fnfSettlementDto: res.payload
          }
        });
        toastr.success(
          'Success',
          'FNF settlement amount has been confirmed successfully.'
        );
      } else {
        dispatch({
          type: rpCollectionsActions.CONFIRM_FNF_AMOUNT,
          payload: {
            isLoadingBalance: false,
            isLoaded: true
          }
        });
        toastr.error(
          'Error',
          res.payload.errorMessage || 'Failed to confirm FNF wallet balance.'
        );
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.CONFIRM_FNF_AMOUNT,
        payload: {
          isLoadingBalance: false,
          isLoaded: true
        }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.CONFIRM_FNF_AMOUNT,
        payload: {
          isLoadingBalance: false,
          isLoaded: true
        }
      });
    }
  };

  return makeApiCall(requestObj);
};

export const approveFnf = (expenseType, fnfDTO, userId) => dispatch => {
  dispatch({
    type: rpCollectionsActions.APPROVE_FNF_AMOUNT,
    payload: { isLoadingBalance: true }
  });

  let requestFnfDTO = { ...fnfDTO };
  delete requestFnfDTO.requestedByUserName;
  delete requestFnfDTO.approvedByUserName;

  requestFnfDTO.approvedBy = userId;
  requestFnfDTO.approvedAt = moment().valueOf();

  let requestMethod = 'POST';
  let params = [{ key: '{expenseType}', val: expenseType }];
  let apiEndPoint = 'approveRPBPFnf';

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    method: requestMethod,
    data: requestFnfDTO,

    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        dispatch({
          type: rpCollectionsActions.APPROVE_FNF_AMOUNT,
          payload: {
            isLoadingBalance: false,
            vanNumber: res.payload.orgCode
              ? 'MLLESP' + expenseType + res.payload.orgCode.replace('-', '')
              : null,
            fnfSettlementDto: res.payload,
            orgCode: res.payload.orgCode
          }
        });
        toastr.success('Success', 'FNF approved successfully.');
        getAmountBalance(res.payload.orgCode, expenseType);
      } else {
        dispatch({
          type: rpCollectionsActions.APPROVE_FNF_AMOUNT,
          payload: {
            isLoadingBalance: false,
            isLoaded: true
          }
        });
        toastr.error('Error', 'Failed to approve FNF.');
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.APPROVE_FNF_AMOUNT,
        payload: {
          isLoadingBalance: false,
          isLoaded: true
        }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.APPROVE_FNF_AMOUNT,
        payload: {
          isLoadingBalance: false,
          isLoaded: true
        }
      });
    }
  };

  return makeApiCall(requestObj);
};

/**
 * @function getHandoverPendingData - Get handover pending data of the user
 * @functionality - Set handoverPendingData, handoverPendingAmount variables in the rpCollectionsReducer to the handover pending data of the user, and the totalamount using the getColumnSum function
 * @param startDate - Start date of the filter
 * @param endDate - End date of the filter
 * @returns void
 */

export const getHandoverPendingData = (
  startDate,
  endDate,
  orgId,
  expenseType
) => dispatch => {
  dispatch({
    type: rpCollectionsActions.GET_HANDOVER_PENDING_DATA,
    payload: { isLoading: true, handoverPendingData: null }
  });

  const params = [
    { key: '{fromDateTime}', val: startDate },
    { key: '{toDateTime}', val: endDate },
    { key: '{expenseType}', val: expenseType }
  ];

  let apiEndPoint = 'getRPCollectionHandoverPendingData';

  if (orgId) {
    params.push({ key: '{orgId}', val: orgId });
    apiEndPoint = 'getRPCollectionHandoverPendingDataAdmin';
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        const sum =
          getColumnSum(res.payload, 'totalRoundOffAmount', null, null) || 0;
        dispatch({
          type: rpCollectionsActions.GET_HANDOVER_PENDING_DATA,
          payload: {
            isLoading: false,
            handoverPendingData: res.payload,
            handoverPendingAmount: sum
          }
        });
      } else {
        dispatch({
          type: rpCollectionsActions.GET_HANDOVER_PENDING_DATA,
          payload: {
            isLoading: false,
            handoverPendingData: [],
            handoverPendingAmount: 0
          }
        });
        toastr.error('Error', 'Failed to get handover pending data');
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.GET_HANDOVER_PENDING_DATA,
        payload: {
          isLoading: false,
          handoverPendingData: [],
          handoverPendingAmount: 0
        }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.GET_HANDOVER_PENDING_DATA,
        payload: {
          isLoading: false,
          handoverPendingData: [],
          handoverPendingAmount: 0
        }
      });
    }
  };

  return makeApiCall(requestObj);
};

/**
 * @function getHandoverCompletedData - Get handover completed data of the user
 * @functionality - Set handoverCompletedData, handoverCompletedAmount variable in the rpCollectionsReducer to the handover completed data of the user, and the totalamount using the getColumnSum function
 * @param startDate - Start date of the filter
 * @param endDate - End date of the filter
 * @returns void
 */

export const getHandoverCompletedData = (
  startDate,
  endDate,
  orgId,
  expenseType
) => dispatch => {
  dispatch({
    type: rpCollectionsActions.GET_HANDOVER_COMPLETED_DATA,
    payload: { isLoading: true, handoverCompletedData: [] }
  });

  const params = [
    { key: '{fromDateTime}', val: startDate },
    { key: '{toDateTime}', val: endDate },
    { key: '{expenseType}', val: expenseType }
  ];

  let apiEndPoint = 'getRPCollectionHandoverCompletedData';

  if (orgId) {
    params.push({ key: '{orgId}', val: orgId });
    apiEndPoint = 'getRPCollectionHandoverCompletedDataAdmin';
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        const sum =
          getColumnSum(res.payload, 'totalRoundOffAmount', null, null) || 0;
        dispatch({
          type: rpCollectionsActions.GET_HANDOVER_COMPLETED_DATA,
          payload: {
            isLoading: false,
            handoverCompletedData: res.payload,
            handoverCompletedAmount: sum
          }
        });
      } else {
        dispatch({
          type: rpCollectionsActions.GET_HANDOVER_COMPLETED_DATA,
          payload: {
            isLoading: false,
            handoverCompletedData: [],
            handoverCompletedAmount: 0
          }
        });
        toastr.error('Error', 'No data found');
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.GET_HANDOVER_COMPLETED_DATA,
        payload: {
          isLoading: false,
          handoverCompletedData: [],
          handoverCompletedAmount: 0
        }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.GET_HANDOVER_COMPLETED_DATA,
        payload: {
          isLoading: false,
          handoverCompletedData: [],
          handoverCompletedAmount: 0
        }
      });
    }
  };

  return makeApiCall(requestObj);
};

/**
 * @function downloadTransactionReport - Download transaction report of the user
 * @functionality - Download the transaction report of the user for the given date range in the xlsx format
 * @param startDate - Start date of the filter
 * @param endDate - End date of the filter
 * @returns (xlsx file) - Transaction report of the user
 */

export const downloadTransactionReport = (
  startDate,
  endDate,
  orgId,
  expenseType
) => dispatch => {
  dispatch({
    type: rpCollectionsActions.SET_LOADER,
    payload: { isLoading: true }
  });

  const params = [
    { key: '{fromDateTime}', val: startDate },
    { key: '{toDateTime}', val: endDate },
    { key: '{expenseType}', val: expenseType }
  ];

  let apiEndPoint = 'downloadRPCollectionTransactionReport';

  if (orgId) {
    params.push({ key: '{orgId}', val: orgId });
    apiEndPoint = 'downloadRPCollectionTransactionReportAdmin';
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    method: 'GET',
    contentType: 'application/xml',
    responseType: 'arraybuffer',
    successCb: res => {
      const fileName = 'Transaction_Report.xlsx';
      // readablesteam to file download
      downloadArrayBufferAsXLS(res, fileName);
    }
  };

  return makeApiCall(requestObj);
};

/**
 * @function knockOffConsignment - Knock off consignment
 * @functionality - Knock off the pending consignment of the user manually if it is eligible for knock off
 * @param consignmentId - Consignment id of the consignment to be knocked off
 * @param callback - Callback function to be called after the api call is successful
 * @returns void
 * @throws Error - If the api call fails
 */

export const knockOffConsignment = (
  consignmentId,
  callback,
  expenseType,
  orgId
) => dispatch => {
  dispatch({
    type: rpCollectionsActions.SET_LOADER,
    payload: { isLoading: true }
  });
  dispatch({
    type: rpCollectionsActions.SET_KNOCK_OFF_LOADER,
    payload: { isKnockOffInProgress: true }
  });

  let apiEndPoint = 'knockOffConsignment';

  const params = [
    { key: '{consignmentId}', val: consignmentId },
    { key: '{expenseType}', val: expenseType }
  ];

  if (orgId) {
    params.push({
      key: '{orgId}',
      val: orgId
    });
    apiEndPoint = 'knockOffConsignmentAdmin';
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: apiEndPoint,
    pathVariables: params,
    method: 'POST',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'Consignment knocked off successfully');
        dispatch({
          type: rpCollectionsActions.SET_LOADER,
          payload: { isLoading: false }
        });
        dispatch({
          type: rpCollectionsActions.SET_KNOCK_OFF_LOADER,
          payload: { isKnockOffInProgress: false }
        });
        callback();
      } else {
        toastr.error('Error', 'Failed to knock off consignment');
        dispatch({
          type: rpCollectionsActions.SET_LOADER,
          payload: { isLoading: false }
        });
        dispatch({
          type: rpCollectionsActions.SET_KNOCK_OFF_LOADER,
          payload: { isKnockOffInProgress: false }
        });
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.SET_LOADER,
        payload: { isLoading: false }
      });
      dispatch({
        type: rpCollectionsActions.SET_KNOCK_OFF_LOADER,
        payload: { isKnockOffInProgress: false }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.SET_LOADER,
        payload: { isLoading: false }
      });
      dispatch({
        type: rpCollectionsActions.SET_KNOCK_OFF_LOADER,
        payload: { isKnockOffInProgress: false }
      });
    }
  };

  return makeApiCall(requestObj);
};

export const getRpListForRpAdmin = expenseType => dispatch => {
  dispatch({
    type: rpCollectionsActions.SET_LOADER,
    payload: { isLoading: true }
  });

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getRpList',
    pathVariables: [{ key: '{expenseType}', val: expenseType }],

    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch({
          type: rpCollectionsActions.SET_LOADER,
          payload: { isLoading: false }
        });
        dispatch({
          type: rpCollectionsActions.SET_RP_LIST,
          payload: res.payload
        });
      } else {
        toastr.error('Error', `Failed to fetch ${expenseType} List`);
        dispatch({
          type: rpCollectionsActions.SET_LOADER,
          payload: { isLoading: false }
        });
      }
    },
    failureCb: err => {
      dispatch({
        type: rpCollectionsActions.SET_LOADER,
        payload: { isLoading: false }
      });
    },
    catchCb: err => {
      dispatch({
        type: rpCollectionsActions.SET_LOADER,
        payload: { isLoading: false }
      });
    }
  };

  return makeApiCall(requestObj);
};
